import {
  AudienceComparisonCellData,
  AudienceComparisonColumn,
} from "../../types/audience-compare";

import { ReactNode } from "react";
import { ResultCell } from "./ResultCell";
import { Tooltip } from "@mantine/core";
import classNames from "classnames";
import css from "./ResultTable.module.css";

export type ResultTableRow = {
  label: string;
  data: AudienceComparisonCellData;
};

type ResultTableProps = {
  label: ReactNode;
  rows: ResultTableRow[];
  columns: AudienceComparisonColumn[];
};

/**
 * Translates an index value to a CSS class (or undefined).
 */
const getIndexColor = (data: AudienceComparisonCellData) => {
  if (data.kind !== "success") {
    return;
  }

  if (data.value >= 190) {
    return css["positive-index-10"];
  }
  if (data.value >= 180) {
    return css["positive-index-09"];
  }
  if (data.value >= 170) {
    return css["positive-index-08"];
  }
  if (data.value >= 160) {
    return css["positive-index-07"];
  }
  if (data.value >= 150) {
    return css["positive-index-06"];
  }
  if (data.value >= 140) {
    return css["positive-index-05"];
  }
  if (data.value >= 130) {
    return css["positive-index-04"];
  }
  if (data.value >= 120) {
    return css["positive-index-03"];
  }
  if (data.value >= 110) {
    return css["positive-index-02"];
  }
  if (data.value > 100) {
    return css["positive-index-01"];
  }
};

/**
 * Displays a single table of results.
 */
function ResultTable({ label, rows, columns }: ResultTableProps) {
  const columnWidthClass = columns.length === 2 ? "w-25" : "w-50";
  return (
    <table
      className={classNames(
        "table table-borderless table-striped mb-0",
        css["results-table"],
      )}
    >
      <thead>
        <tr>
          <th className="w-50">{label}</th>
          {columns.includes("index") && (
            <th className={classNames("text-center", columnWidthClass)}>
              Index
            </th>
          )}
          {columns.includes("composition") && (
            <th className={classNames("text-center", columnWidthClass)}>
              Composition
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            {columns.includes("index") && (
              <ResultCell
                cell={r.data}
                columnKind="index"
                renderSuccess={({ value }) =>
                  value.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })
                }
                className={getIndexColor(r.data)}
              />
            )}
            {columns.includes("composition") && (
              <ResultCell
                cell={r.data}
                columnKind="composition"
                renderSuccess={({ baselinePct, scorePct }) => (
                  <Tooltip
                    label={
                      "Baseline: " +
                      baselinePct?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        style: "percent",
                      })
                    }
                  >
                    <span>
                      {scorePct?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        style: "percent",
                      })}
                    </span>
                  </Tooltip>
                )}
              />
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default ResultTable;
